import type { UnionToIntersection } from 'type-fest';

import type {
  THygieneTaskType,
  TTreatmentType,
} from '@/react-query/insights/types';

import {
  ConfidenceThresholdValues,
  binValues,
} from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import type {
  BinValues,
  ConfidenceScoreSliderValues,
} from '../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import type { TToothProcedureNames } from '../react-query/useVisitSummaries';
export type DevicePreferences = {
  scrollToCurrent: string;
};

export type InsightsDashboard =
  | 'CLINICAL'
  | 'HYGIENE'
  | 'ENGAGEMENT'
  | 'BENCHMARKS';

export type PreferenceUpdateBody =
  | { enableVideaReferral: boolean }
  | { oralinxReferralRequestVersion: number }
  | { fluorideTaskAgeLimit: number }
  | { useNewTreatmentGroups: boolean }
  | { adjustmentVersion: string }
  | { binThreshold?: BinValues }
  | { cdnEnabled: boolean }
  | { cleanClaims: boolean }
  | { confidenceThreshold: ConfidenceScoreSliderValues }
  | { crownIndicatorMinimumDmf?: number }
  | { crownOpportunity: boolean }
  | { DDAISharpeningEnabled: boolean }
  | { displayDentalPhotos?: boolean | 'disabled' | 'off' | 'on' }
  | { displayDentalThumbnails?: boolean }
  | { displayPRLSegmentations: boolean }
  | { displayRALSegmentations: boolean }
  | { dmfEnabled: boolean }
  | { enableAiRerun: boolean }
  | { allowRerunAnalysesFromBridgeLinkEvent: boolean }
  | { enableBatchVisitSummariesProcessing: boolean }
  | { enableDevDashboards: boolean }
  | { enableHqOnThumbnails: boolean }
  | { enableLatestImagesV2?: boolean }
  | { enableOrgWideSearch: boolean }
  | { enablePatientsV2: boolean }
  | { enableRecommendationInteractions: boolean }
  | { enableScanOpportunityTask?: boolean }
  | { enableScanOpportunityTask?: boolean }
  | { enableSeriesView: boolean }
  | { fmxSummaryEnabled: boolean }
  | { hasVaultPractice: boolean }
  | { historicalTriggerEnabled: boolean }
  | { hqOffByDefault: boolean }
  | { huddleDisabled: boolean }
  | { huddleVersion: 'v1' | 'v2' }
  | { incipientIndicatorEnabled: boolean }
  | { incipientOpportunity: boolean }
  | {
      indicatorCopy: {
        crown?: string;
        extraction?: string;
        filling?: string;
        implant?: string;
        incipient?: string;
        ortho?: string;
        rootCanal?: string;
        srp?: string;
        locSrp?: string;
      };
    }
  | { insSummaryModal: boolean }
  | { isInsuranceEligibilityEnabled: boolean }
  | { insuranceExportPreviewPages?: 'paginated' | 'single' }
  | { isPlanningModeDisabled?: boolean }
  | { isVideaNotesEnabled: boolean }
  | { onboardingPopupEnabled: boolean }
  | { preferDisplayOverSharpened?: boolean }
  | { preferDisplayOverSharpened?: boolean }
  | { restorativeOpportunity: boolean }
  | { rolloutAverageDmfImageViewer: boolean }
  | { sageTxRolloutFlag: boolean }
  | { segmentationEnabled: boolean }
  | { showPlannedTxInIndicator: boolean }
  | { srpIndicatorEnabled: boolean }
  | { rootCanalIndicatorEnabled: boolean }
  | { rootCanalOpportunity: boolean }
  | { extractionIndicatorEnabled: boolean }
  | { extractionOpportunity: boolean }
  | { fillingIndicatorEnabled: boolean }
  | { fillingOpportunity: boolean }
  | { alignerIndicatorEnabled: boolean }
  | { alignerOpportunity: boolean }
  | { implantCrownIndicatorEnabled: boolean }
  | { implantCrownOpportunity: boolean }
  | { locSrpIndicatorEnabled: boolean }
  | { locSrpOpportunity: boolean }
  | { srpOpportunity: boolean }
  | { standalonePollingInterval: number }
  | { taskBitewings: number }
  | { taskFluoride: number }
  | { taskFmx: number }
  | { taskNextAppointment: boolean }
  | { taskPerioChart: number }
  | { taskPerioMaintenance: number }
  | { taskProphy: number }
  | { taskScanOpportunity: number }
  | { toothLocalization?: 'fdi' | 'universal' }
  | { unscheduledScheduledToggleEnabled?: boolean }
  | {
      examplePreference: string;
    }
  | {
      srpClaimSummaryThresholds: {
        bwBoneLossThreshold?: number | undefined;
        paBoneLossThreshold?: number | undefined;
      };
    }
  | {
      enableInsights?: InsightsDashboard[] | false;
    }
  | { enableInsightsPOC?: boolean }
  | { narrativeDevToolEnabled: boolean }
  | { showSegmentationView?: boolean }
  | { saveSegmentationViewEnabled?: boolean }
  | { canOptInImageViewerRedesign?: boolean }
  | { enableCustomPracticeGroups?: boolean }
  | { narrativeDevToolEnabled: boolean }
  | { enableMultiSource?: boolean }
  | { showUnknownViz: boolean }
  | { useSidebarRedesign: boolean }
  | { spotlightProcedure: TToothProcedureNames | 'none' }
  | { newImageAnnotatorThumbnailsForClaims: boolean }
  | { localeSwitcherEnabled: boolean }
  | { enableInsightsPilotRegion: string }
  | { forceEnableRecommendations?: boolean }
  | { enableBenchmarksOnAllPages?: boolean }
  | { showQRCodeButton?: boolean }
  | { insightsEnabledRecos?: TTreatmentType[] }
  | { insightsEnabledTasks?: THygieneTaskType[] }
  | { disableAdjustmentForPanos?: boolean }
  | { claimAttachmentPdfEnabled?: boolean }
  | { disablePracticePreferences?: boolean }
  | { isProviderNameDisabledInAttachment?: boolean }
  | { standaloneClaimsEnabled?: boolean }
  | {
      customPatientEducationTxCopy?: {
        filling?: string;
        srp?: string;
        crown?: string;
        rct?: string;
        tooth_remineralization?: string;
        implant?: string;
        crown_pediatric?: string;
        enamel_regen_pediatric?: string;
        fluoride?: string;
        spacer?: string;
        sealant?: string;
      };
    }
  | {
      allowEditingOrgLevelPreferences?: {
        customPatientEducationTxCopy?: boolean;
      };
    }
  | { showParentheticalTxNames?: boolean }
  | { claimAttachmentPerioChartEnabled?: boolean }
  | { isFeedbackWidgetEnabled?: boolean }
  | { enableClickBasedFindingInteraction?: boolean }
  | { enableInsightsEngagementMetric?: boolean }
  | { referralsPatientIdEnabled?: boolean }
  | { enableQuickNotes?: boolean }
  | { enableVoiceNotesConsent?: boolean }
  | { enableVoiceNotesStandalone?: boolean }
  | { enableImageQualityAssessment?: boolean }
  | { enableClinicalInteractionsForDirectors?: boolean }
  | { enableQuickNotesPatientId?: boolean }
  | { enableSpecialistSearchRedesign?: boolean };

// Remember to add the preference to preferencesMetaData below if applicable!

type ExtractPreferenceKeys<T> = T extends { [key: string]: any }
  ? keyof T
  : never;

export type PreferenceKey = ExtractPreferenceKeys<PreferenceUpdateBody>;

// Type utility to extract the type of a specific preference
export type TPreferences = {
  [K in PreferenceKey]: Extract<PreferenceUpdateBody, { [P in K]: any }>[K];
};

interface PreferenceOption {
  key: PreferenceKey;
  type: readonly ('boolean' | 'string' | 'number')[];
  possibleValues: readonly (string | number)[];
}

// This `preferencesMetaData` constant should only contain preferences that are
// string (with fixed set of options), number (with fixed set of options),
// boolean and string, AND boolean and number.
export const preferencesMetaData: Partial<
  Record<PreferenceKey, PreferenceOption>
> = {
  binThreshold: {
    key: 'binThreshold',
    type: ['number'],
    possibleValues: binValues,
  },
  confidenceThreshold: {
    key: 'confidenceThreshold',
    type: ['number'],
    possibleValues: ConfidenceThresholdValues,
  },
  displayDentalPhotos: {
    key: 'displayDentalPhotos',
    type: ['boolean', 'string'],
    possibleValues: ['disabled', 'off', 'on'],
  },
  huddleVersion: {
    key: 'huddleVersion',
    type: ['string'],
    possibleValues: ['v1', 'v2'],
  },
  insuranceExportPreviewPages: {
    key: 'insuranceExportPreviewPages',
    type: ['string'],
    possibleValues: ['paginated', 'single'],
  },
  toothLocalization: {
    key: 'toothLocalization',
    type: ['string'],
    possibleValues: ['fdi', 'universal'],
  },
} as const;

export type PreferencesResponse = Partial<
  UnionToIntersection<PreferenceUpdateBody>
>;
